// Shared helpers for landing on a section after navigation. The slug scheme
// must stay in sync with scripts/build-search-index.mjs and the in-page
// assignAnchors() helpers in SearchPalette.vue / IndexPage.vue.
export function slugify(s) {
  return String(s)
    .replace(/<[^>]+>/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

// Assign slug ids to the current page's headings / timeline titles, mirroring
// the build-time extractor, so a source link can deep-link to its section.
export function assignAnchors(root) {
  if (!root) return;
  const useEntries = !!root.querySelector('li.title');
  const els = useEntries ? root.querySelectorAll('li.title') : root.querySelectorAll('h2, h3');
  const seen = new Set();
  els.forEach((el) => {
    const base = slugify(el.getAttribute('data-source-anchor') || el.textContent);
    if (!base) return;
    let slug = base;
    let n = 2;
    while (seen.has(slug)) slug = `${base}-${n++}`;
    seen.add(slug);
    el.id = slug;
  });
}

// Land on the entry's visual top, not the title: research/projects keep the
// date label at the top (.timeline-event); education/experience keep the
// school/company block top (the entry <section>).
export function scrollTargetFor(el) {
  const te = el.closest('.timeline-event');
  if (te) return te;
  if (el.matches && el.matches('li.title')) {
    const sec = el.closest('section');
    if (sec) return sec;
  }
  return el;
}

// A citation may point into an implementation disclosure. Open only the
// containing panels through their own controls, preserving Vue's state.
function containingDisclosureControls(el) {
  const controls = [];
  let node = el;
  while (node && node !== document.body) {
    if (node.matches?.('.impl-details-panel')) {
      const control = node.closest('.impl-details')?.querySelector('.impl-details-toggle');
      if (control?.getAttribute('aria-controls') === node.id
        && control.getAttribute('aria-expanded') === 'false') controls.push(control);
    }
    node = node.parentElement;
  }
  return controls.reverse();
}

// Reveal animations start entries translated down 16-20px; the transform
// resolves when the entry enters the viewport. scrollIntoView aims at the
// transformed box and lands short once the transform settles, so read the
// settled layout position instead.
function revealTranslateY(el) {
  let total = 0;
  let node = el;
  while (node && node !== document.body) {
    const tr = getComputedStyle(node).transform;
    if (tr && tr !== 'none') {
      const m2 = tr.match(/^matrix\(([^)]+)\)$/);
      if (m2) {
        const parts = m2[1].split(',').map(parseFloat);
        if (parts.length >= 6) total += parts[5];
      } else {
        const m3 = tr.match(/^matrix3d\(([^)]+)\)$/);
        if (m3) {
          const parts = m3[1].split(',').map(parseFloat);
          if (parts.length >= 16) total += parts[13];
        }
      }
    }
    node = node.parentElement;
  }
  return total;
}

// The document position of the element with every reveal transform (translate
// AND scale) neutralised, i.e. where it will sit once the reveal settles.
function settledDocumentTop(el) {
  const changed = [];
  let node = el;
  while (node && node !== document.body) {
    const tr = getComputedStyle(node).transform;
    if (tr && tr !== 'none' && tr !== 'matrix(1, 0, 0, 1, 0, 0)') {
      changed.push([node, node.style.getPropertyValue('transform'), node.style.getPropertyPriority('transform')]);
      node.style.transform = 'none';
    }
    node = node.parentElement;
  }
  const top = el.getBoundingClientRect().top + window.scrollY;
  changed.forEach(([n, value, priority]) => {
    if (value) n.style.setProperty('transform', value, priority);
    else n.style.removeProperty('transform');
  });
  return top;
}

export function landingOffset(target) {
  const authoredMargin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  let chapterBottom = 0;
  const overlayBottom = ['.header', '.chapter-rail:not(.rail-static)', '.case-navigation']
    .map((selector) => document.querySelector(selector))
    .filter(Boolean)
    .reduce((bottom, element) => {
      const style = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const overlaysPage = (style.position === 'fixed' || style.position === 'sticky')
        && style.display !== 'none'
        && style.visibility !== 'hidden'
        && rect.bottom > 0;
      if (!overlaysPage) return bottom;
      const occupiedBottom = style.position === 'sticky'
        ? (parseFloat(style.top) || 0) + rect.height
        : rect.bottom;
      if (element.matches('.case-navigation')) chapterBottom = occupiedBottom;
      const clearance = parseFloat(style.getPropertyValue('--anchor-clearance')) || 0;
      return Math.max(bottom, occupiedBottom + clearance);
    }, 0);

  // The return control moves below the chapter bar. Reserve its final position
  // when it overlaps the destination heading, even before the bar has pinned.
  const targetRect = target.getBoundingClientRect();
  const back = document.querySelector('.site-back-global, .view-return-slot');
  let returnBottom = 0;
  // Collection entries align under the site header. The floating return cue
  // is not a full-width toolbar and must not add another row to their offset.
  const collectionTarget = target.matches?.('.collection-card, .collection-index-unit, .timeline-event');
  if (back && !collectionTarget) {
    const style = getComputedStyle(back);
    const rect = back.getBoundingClientRect();
    if (style.position === 'fixed' && style.display !== 'none'
      && rect.right > targetRect.left && rect.left < targetRect.right) {
      returnBottom = Math.max(parseFloat(style.top) || 0, chapterBottom ? chapterBottom + 8 : 0) + rect.height;
    }
  }
  if (!overlayBottom && !returnBottom) return authoredMargin;
  const breathingRoom = window.matchMedia?.('(max-width: 760px)').matches ? 12 : 16;
  return Math.max(authoredMargin, Math.ceil(Math.max(overlayBottom, returnBottom) - 0.01) + breathingRoom);
}

// Group auto-expansion is disabled (groups are always expanded), so no
// expander hook is registered.
// function expandEntryForLanding(id) {
//   if (typeof window !== 'undefined' && typeof window.__expandEntryForLanding === 'function') {
//     try {
//       window.__expandEntryForLanding(id);
//     } catch (e) { /* expansion is best-effort */ }
//   }
// }

// Smoothly scroll to an element id, honouring the target's own scroll-margin
// and the reveal animation's settled position. Returns true when the element
// was found and scrolled to.
export function landOnId(id, { smooth = true, root = null } = {}) {
  if (!id) return false;
  // expandEntryForLanding(id);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = smooth && !reduceMotion ? 'smooth' : 'auto';

  let el = document.getElementById(id);
  if (!el) {
    assignAnchors(root || document.querySelector('.content-container'));
    el = document.getElementById(id);
  }
  if (!el) return false;

  if (containingDisclosureControls(el).length) {
    return settleScrollToId(id, { smooth, root });
  }
  const target = scrollTargetFor(el);
  // Start the landing block's reveal now so its transform settles before (or
  // while) we arrive, then scroll to the settled layout position.
  let node = target;
  while (node && node !== document.body) {
    if (node.classList && node.classList.contains('scroll-reveal-item')) {
      node.classList.add('scroll-reveal-visible');
    }
    node.classList?.remove('case-reveal-pending');
    node = node.parentElement;
  }

  const margin = landingOffset(target);
  const rect = target.getBoundingClientRect();
  const top = Math.max(0, rect.top + window.scrollY - revealTranslateY(target) - margin);
  window.scrollTo({ top, behavior });
  return true;
}

let activeSettleStop = null;

export function cancelAnchorLanding() {
  activeSettleStop?.();
}

// One glide, after the layout has settled. The main drift source is webfonts:
// waiting for them (capped) means the smooth scroll starts from the final
// geometry, so it lands exactly once instead of correcting mid-way. A light
// monitor still catches rare late reflow (an image that did not reserve its
// space) and glides again if the target drifts. The monitor stops on user
// scroll/touch/keyboard takeover or after maxWait.
export function settleScrollToId(id, { smooth = true, root = null, maxWait = 3500 } = {}) {
  if (!id) return false;
  // expandEntryForLanding(id);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = smooth && !reduceMotion ? 'smooth' : 'auto';

  let el = document.getElementById(id);
  if (!el) {
    assignAnchors(root || document.querySelector('.content-container'));
    el = document.getElementById(id);
  }
  if (!el) return false;

  const disclosureControls = containingDisclosureControls(el);
  disclosureControls.forEach(control => control.click());
  const target = scrollTargetFor(el);
  // Start the landing block's reveal now. The reveal transition settles the
  // element up by ~16px, so we wait for it to finish before computing the
  // final position; otherwise the glide lands too high and then never drifts
  // back (there is no correction for a settled transform).
  const revealChain = [];
  let node = target;
  while (node && node !== document.body) {
    if (node.classList && node.classList.contains('scroll-reveal-item')) {
      node.classList.add('scroll-reveal-visible');
      revealChain.push(node);
    }
    if (node.classList?.contains('case-reveal-pending')) {
      node.classList.remove('case-reveal-pending');
      revealChain.push(node);
    }
    node = node.parentElement;
  }
  const isSettledTransform = (tr) => !tr || tr === 'none' || tr === 'matrix(1, 0, 0, 1, 0, 0)';
  const revealSettling = revealChain.some((n) => !isSettledTransform(getComputedStyle(n).transform));

  let live = true;
  let launched = false;
  let checkTimer = null;
  const go = (behavior = 'smooth') => {
    if (!live || !target.isConnected) return;
    const top = Math.max(0, settledDocumentTop(target) - landingOffset(target));
    window.scrollTo({ top, behavior });
  };
  const check = () => {
    if (!live || !launched) return;
    if (checkTimer) window.clearTimeout(checkTimer);
    // Wait until the glide has stopped, including on long pages where it can
    // take more than a second. Correcting during the native smooth scroll can
    // leave one of its queued frames moving the page past the final target.
    checkTimer = window.setTimeout(() => {
      if (!live) return;
      if (!target.isConnected) {
        stop();
        return;
      }
      const drift = Math.abs(target.getBoundingClientRect().top - landingOffset(target));
      // Late content can move the destination after the initial glide. Keep
      // the requested motion policy so that correction never snaps the page.
      if (drift > 1) go(behavior);
    }, 150);
  };
  const stop = () => {
    if (!live) return;
    live = false;
    if (checkTimer) window.clearTimeout(checkTimer);
    if (activeSettleStop === stop) activeSettleStop = null;
    window.removeEventListener('wheel', stop, { passive: true });
    window.removeEventListener('touchstart', stop, { passive: true });
    window.removeEventListener('keydown', stop);
    window.removeEventListener('scroll', check);
  };

  // Only one settle monitor at a time: a newer click or navigation supersedes
  // an older landing, so stale corrections never fight the latest intent.
  if (activeSettleStop) activeSettleStop();
  activeSettleStop = stop;

  const launch = () => {
    if (!live || launched) return;
    launched = true;
    // A trailing input event during the reveal/font wait must not discard the
    // destination. Once the glide starts, visitor scrolling can take over.
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    window.addEventListener('scroll', check, { passive: true });
    go(behavior);
    [...document.images].forEach((im) => {
      if (!im.complete) {
        im.addEventListener('load', check, { once: true });
        im.addEventListener('error', check, { once: true });
      }
    });
    window.addEventListener('load', check, { once: true });
    // A reveal transition can still be settling after the initial bounded
    // wait, even when every image and font is already ready. Recheck once
    // after that motion has finished so the heading does not stop just above
    // the intended reading position.
    window.setTimeout(check, 650);
    window.setTimeout(stop, maxWait);
  };

  const fonts = document.fonts && document.fonts.ready;
  const fontsReady = fonts ? fonts.catch(() => {}) : Promise.resolve();
  const revealSettle = revealSettling || disclosureControls.length
    ? new Promise((resolve) => window.setTimeout(resolve, 450))
    : Promise.resolve();
  Promise.all([fontsReady, revealSettle]).then(launch);
  // Overall cap so the glide still starts promptly on slow resources.
  window.setTimeout(launch, 1000);
  return true;
}

// Glide to a saved absolute scroll offset once layout has settled (fonts ready,
// the first reveal settle done, images that reserved space loaded). Used when a
// "back to origin" returns to a pixel position instead of an anchor: scrolling
// before the page finishes laying out lands short, and late layout then drifts
// the page away from the saved offset.
export function settleScrollToY(y, { maxWait = 3500 } = {}) {
  const targetY = Math.max(0, Number(y) || 0);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduceMotion ? 'auto' : 'smooth';

  let live = true;
  let launched = false;
  let driftTimer = null;

  const go = () => {
    if (!live) return;
    const doc = document.documentElement || document.body;
    const maxY = Math.max(0, (doc.scrollHeight || document.body.scrollHeight) - window.innerHeight);
    window.scrollTo({ top: Math.min(targetY, maxY), behavior });
  };
  const stop = () => {
    if (!live) return;
    live = false;
    window.clearTimeout(driftTimer);
    window.removeEventListener('wheel', stop, { passive: true });
    window.removeEventListener('touchstart', stop, { passive: true });
    window.removeEventListener('keydown', stop);
  };

  const launch = () => {
    if (!live || launched) return;
    launched = true;
    go();
    // A light monitor corrects once if late layout (images, fonts, reveal
    // transforms) drifts the document after the first glide.
    driftTimer = window.setTimeout(() => {
      if (!live) return;
      if (Math.abs(window.scrollY - targetY) > 4) go();
    }, 700);
  };

  const fonts = document.fonts && document.fonts.ready;
  const fontsReady = fonts ? fonts.catch(() => {}) : Promise.resolve();
  Promise.all([
    fontsReady,
    new Promise((resolve) => window.setTimeout(resolve, 300)),
  ]).then(launch);
  window.setTimeout(launch, 1000);
  window.setTimeout(stop, maxWait);

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
}
